'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

const supabase = createClient();

const ROLE_KEY = 'clinigol_user_role';

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  userRole: string | null;
  userProfile: any;
  isAdmin: boolean;
  isOwner: boolean;
  isVet: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  // loading = true until the FIRST session check completes
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  // Prevent duplicate role fetches
  const roleFetchRef = useRef<string | null>(null);

  const persistRole = (role: string) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(ROLE_KEY, role);
    }
    setUserRole(role);
  };

  const clearRole = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(ROLE_KEY);
    }
    setUserRole(null);
    roleFetchRef.current = null;
  };

  const fetchUserRole = useCallback(async (userId: string) => {
    // Skip if already fetched for this user
    if (roleFetchRef.current === userId) return;
    roleFetchRef.current = userId;

    // Check sessionStorage cache first for instant role resolution
    if (typeof window !== 'undefined') {
      const cached = sessionStorage.getItem(ROLE_KEY);
      if (cached) {
        setUserRole(cached);
        // Still fetch from DB in background to keep it fresh
      }
    }

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role, full_name, dog_name, dog_age, dog_breed, vet_name')
        .eq('id', userId)
        .single();

      if (!error && data?.role) {
        persistRole(data.role);
        setUserProfile(data);
      } else {
        // Fallback to auth metadata
        const { data: { user: authUser } } = await supabase.auth.getUser();
        const metaRole =
          authUser?.user_metadata?.role ??
          authUser?.app_metadata?.role ??
          'owner';
        persistRole(metaRole);
      }
    } catch {
      // Last resort
      const cached = typeof window !== 'undefined' ? sessionStorage.getItem(ROLE_KEY) : null;
      if (!cached) persistRole('owner');
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Single initialization — get the current session once
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!mounted) return;

      setSession(initialSession);
      setUser(initialSession?.user ?? null);

      if (initialSession?.user) {
        // Fetch role, then mark loading done
        fetchUserRole(initialSession.user.id).finally(() => {
          if (mounted) setLoading(false);
        });
      } else {
        clearRole();
        setLoading(false);
      }
    });

    // Auth state changes — update state but do NOT touch loading
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;

      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (newSession?.user) {
        // Only re-fetch role if it's a different user or sign-in event
        if (event === 'SIGNED_IN' || roleFetchRef.current !== newSession.user.id) {
          roleFetchRef.current = null; // allow re-fetch on new sign-in
          fetchUserRole(newSession.user.id);
        }
      } else {
        clearRole();
        setUserProfile(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserRole]);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'owner',
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    clearRole();
    setUserProfile(null);
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at != null;

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase.from('user_profiles').select('*').eq('id', user.id).single();
    if (error) throw error;
    return data;
  };

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    roleFetchRef.current = null; // force re-fetch
    await fetchUserRole(user.id);
  }, [user, fetchUserRole]);

  const value: AuthContextType = {
    user,
    session,
    loading,
    userRole,
    userProfile,
    isAdmin: userRole === 'admin',
    isOwner: userRole === 'owner',
    isVet: userRole === 'vet',
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
